import { create } from 'zustand';
import type { SyncTransport } from '@/lib/sync/sync-transport';
import { noteAiStatus } from '@/lib/companion/companion-activity';
import { capture } from '@/lib/analytics/capture';
import {
  ConversationRegistry,
  type ConversationStatus,
} from '../utils/conversation-registry';
import { attachLivenessConsumer } from '../utils/conversation-liveness-consumer';

/**
 * Connection-scoped conversation registry store (feature-owned per repo
 * convention). Holds the ConversationRegistry (Chat instances, NOT a parallel
 * buffer — design §8.2) and mirrors its status into reactive state so badges
 * re-render when a BACKGROUND run completes with no tab mounted.
 *
 * Replaces the global use-assistant-store `currentConversationId` scalar.
 */
type WarmChat = { dispose: () => void };

interface ConversationRegistryState {
  /** Reactive mirror of per-conversation liveness for badge rendering. */
  statuses: Record<string, ConversationStatus>;
  /** The conversation currently rendered in the chat view (was the scalar). */
  currentConversationId: string | null;
  /**
   * The conversation whose tab is ACTIVELY FOCUSED in the workspace right now.
   * Written exclusively by WorkspaceView (via useTrackFocusedConversation) when
   * the focused panel's active tab is a conversation. Null when the focused panel
   * is showing a non-conversation tab. Badge gates and grace eviction read this
   * value — not currentConversationId — so a stale continuity pointer never
   * suppresses a background-completion badge.
   */
  focusedConversationId: string | null;

  init: (syncTransport: SyncTransport) => void;
  /**
   * Tear down the firehose consumer and clear all grace timers. Call from
   * useEffect cleanup keyed on [syncTransport] in AssistantChatProvider so
   * workspace teardown / logout doesn't leak handlers.
   */
  teardown: () => void;
  acquireChat: (id: string, factory: () => WarmChat) => WarmChat;
  releaseChat: (id: string) => void;
  /** Sets a conversation's status to 'running'. Currently driven by the liveness
   * consumer via the firehose; exposed as a store action for future direct use. */
  markRunning: (id: string) => void;
  /** Sets a conversation's status to 'needs-approval'. Exempt from grace eviction
   * (a pending tool-approval must not be silently dropped on tab unmount). */
  markNeedsApproval: (id: string) => void;
  setCurrent: (id: string | null) => void;
  /** Sets focusedConversationId. Called from useTrackFocusedConversation in WorkspaceView. */
  setFocused: (id: string | null) => void;
  /**
   * The conversation the ambient assistant (overlay + sidebar) is currently
   * showing — null until the user's first send promotes the ambient chat's
   * minted UUID into a real conversation. WorkspaceAssistantChatProvider keys
   * its provider on this so expand-to-tab and the sidebar render the SAME live
   * bundle by UUID (no snapshot copy). Reset to null starts a fresh ambient chat.
   */
  ambientConversationId: string | null;
  /** Set/clear the ambient conversation pointer. */
  setAmbientConversationId: (id: string | null) => void;
  /**
   * Enumerate every conversation the registry currently marks `running` — the
   * single liveness owner's source of truth for the reconnect coordinator.
   * chatId === conversationId in this project (one key across all layers), so
   * each entry carries both fields equal.
   */
  runningConversations: () => Array<{ chatId: string; conversationId: string }>;
  getWarmChat: (id: string) => WarmChat | null;
  evictNow: (id: string) => void;

  /** @internal test-only reset. */
  __resetForTests: () => void;
}

// Module-level singletons: mount-INDEPENDENT (design §8.1). NOT per-provider.
let registry = new ConversationRegistry<WarmChat>();
let detachConsumer: (() => void) | null = null;
// Track which transport the consumer is currently subscribed to. True idempotency
// is only when the SAME transport is passed to init — a different transport
// (workspace switch) requires detaching the old consumer and re-subscribing.
let currentTransport: SyncTransport | null = null;
// Number of AssistantChatProviders currently mounted. The firehose consumer is
// subscribed on 0→1 (first mount) and detached on 1→0 (last unmount). A tab
// unmount calls teardown() but the workspace overlay is still mounted — without
// refcounting a single teardown would detach the consumer even though a second
// provider still needs liveness running.
// A workspace switch (transport identity change) re-subscribes immediately
// regardless of count — the transport replaces itself for ALL providers at once.
let providerCount = 0;

// A done conversation stays warm for this grace window before its Chat is
// dropped (design §8.3). Running and focused conversations are exempt.
const GRACE_MS = 30_000;
const graceTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Conversations that hit needs-approval at any point in their current run.
// The finished event reports whether the run was ever gated, which cannot be
// read from the statuses at completion — by then the approval is resolved.
const approvalSeen = new Set<string>();

export const useConversationRegistryStore = create<ConversationRegistryState>((set, get) => {
  // Mirror registry status into reactive state. Called after any setStatus.
  const syncStatus = (id: string) => {
    const status = registry.getStatus(id);
    if (status === undefined) return;
    set((s) => ({ statuses: { ...s.statuses, [id]: status } }));
  };

  return {
    statuses: {},
    currentConversationId: null,
    focusedConversationId: null,
    ambientConversationId: null,

    init: (syncTransport) => {
      // Transport-identity check: a workspace switch (new syncTransport) must
      // detach the old consumer and re-subscribe regardless of providerCount —
      // the new transport replaces the old one for ALL providers simultaneously.
      // When the same transport is already wired, only increment the refcount
      // (idempotent subscription — the consumer is already live).
      if (detachConsumer && currentTransport === syncTransport) {
        // Same transport, already subscribed — just track the additional mount.
        providerCount += 1;
        return;
      }

      // Different transport (workspace switch) or first init: detach any
      // existing consumer, then re-subscribe to the new transport.
      detachConsumer?.();
      detachConsumer = null;
      // A workspace switch remounts all providers — the count churns through
      // teardown/init pairs but the new transport must take over immediately.
      // Reset count to 1 for the current (re-)subscribing provider.
      providerCount = 1;
      currentTransport = syncTransport;

      // Wrap the registry's setStatus so each transition mirrors to React state.
      // Re-apply on every (re-)init so the wrapper always calls the current
      // Zustand `set` closure and the fresh syncStatus helper.
      const origSetStatus = registry.setStatus.bind(registry);
      registry.setStatus = (id: string, status: ConversationStatus) => {
        const wasRunning = get().runningConversations().length > 0;
        origSetStatus(id, status);
        syncStatus(id);

        const nowRunning = get().runningConversations().length > 0;
        // Derived from ALL conversation statuses, not just this transition —
        // a different conversation finishing must not clobber a live
        // needs-approval on another one.
        const allStatuses = Object.values(get().statuses);
        if (allStatuses.includes('needs-approval')) {
          noteAiStatus('needs-approval');
        } else {
          noteAiStatus(allStatuses.includes('running') ? 'running' : 'idle');
        }
        if (status === 'needs-approval') {
          approvalSeen.add(id);
          capture('assistant_run_needs_approval', {});
        }
        // Only the transition out of the last running run is worth celebrating —
        // one conversation finishing while others continue is not an ending.
        if (wasRunning && !nowRunning && status === 'done') {
          capture('assistant_run_finished', { had_approval: approvalSeen.has(id) });
          approvalSeen.delete(id);
        }

        if (status === 'done') {
          const existing = graceTimers.get(id);
          if (existing) clearTimeout(existing);
          graceTimers.set(
            id,
            setTimeout(() => {
              graceTimers.delete(id);
              // Evict only if still done AND not the focused conversation.
              if (registry.getStatus(id) === 'done' && get().focusedConversationId !== id) {
                registry.evict(id);
              }
            }, GRACE_MS),
          );
        }
      };
      detachConsumer = attachLivenessConsumer(syncTransport, registry);
    },

    teardown: () => {
      providerCount = Math.max(0, providerCount - 1);
      // Only detach the firehose consumer and clear grace timers when the LAST
      // provider unmounts. A tab unmount calls teardown() but the workspace
      // overlay provider remains mounted — detaching early would break liveness
      // for background runs while the user is on a document tab (§8.4).
      if (providerCount > 0) return;
      graceTimers.forEach((tmr) => clearTimeout(tmr));
      graceTimers.clear();
      approvalSeen.clear();
      detachConsumer?.();
      detachConsumer = null;
      currentTransport = null;
    },

    acquireChat: (id, factory) => {
      const chat = registry.acquire(id, factory);
      registry.enforceLru();
      return chat;
    },

    releaseChat: (id) => {
      registry.release(id);
    },

    markRunning: (id) => {
      // Status set via registry.setStatus (wrapped to mirror to reactive state).
      // Called by AssistantTransport when it sends the run frame: KIND_AGENT_RUN
      // travels C->S only, so no inbound frame can report a run as started.
      registry.setStatus(id, 'running');
    },

    markNeedsApproval: (id) => {
      registry.setStatus(id, 'needs-approval');
    },

    setCurrent: (id) => set({ currentConversationId: id }),

    setFocused: (id) => set({ focusedConversationId: id }),

    setAmbientConversationId: (id) => set({ ambientConversationId: id }),

    runningConversations: () => {
      const { statuses } = get();
      const out: Array<{ chatId: string; conversationId: string }> = [];
      for (const [id, status] of Object.entries(statuses)) {
        if (status === 'running') out.push({ chatId: id, conversationId: id });
      }
      return out;
    },

    getWarmChat: (id) => (registry.getChat(id) as WarmChat | null) ?? null,

    evictNow: (id) => registry.evict(id),

    __resetForTests: () => {
      graceTimers.forEach((tmr) => clearTimeout(tmr));
      graceTimers.clear();
      approvalSeen.clear();
      detachConsumer?.();
      detachConsumer = null;
      currentTransport = null;
      providerCount = 0;
      registry = new ConversationRegistry<WarmChat>();
      set({ statuses: {}, currentConversationId: null, focusedConversationId: null, ambientConversationId: null });
    },
  };
});
